import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
  Stack,
  
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  Star as ShortlistedIcon,
  
} from '@mui/icons-material';
import socketService from '../../services/SocketService';
import ApiService from '../../services/api';

const AllJobApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [employerFilter, setEmployerFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [jobsOptions, setJobsOptions] = useState([]);
  const [employersOptions, setEmployersOptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const formatLocation = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    if (typeof loc === 'object') {
      const { address, city, state, country, zipCode } = loc;
      return [address, city, state, country, zipCode].filter(Boolean).join(', ');
    }
    return String(loc);
  };

  // Fetch applications from backend
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        page: page + 1,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (jobFilter !== 'all') params.jobId = jobFilter;
      if (employerFilter !== 'all') params.employerId = employerFilter;

      const res = await ApiService.getApplications(params);
      const payload = res?.applications || res?.data?.applications || res?.data?.data?.applications || [];
      const pagination = res?.pagination || res?.data?.pagination || res?.data?.data?.pagination || {};

      const mapped = payload.map((a) => ({
        id: a?._id || a?.id,
        applicantName: [a?.applicant?.firstName, a?.applicant?.lastName].filter(Boolean).join(' ') || 'Unknown',
        applicantEmail: a?.applicant?.email || '',
        applicantPhone: a?.applicant?.phone || '',
        jobTitle: a?.job?.title || '—',
        jobId: a?.job?._id || a?.job || '—',
        employerName: a?.employer?.companyName || [a?.employer?.firstName, a?.employer?.lastName].filter(Boolean).join(' ') || '—',
        employerId: a?.employer?._id || '',
        status: a?.status || 'pending',
        appliedDate: a?.createdAt ? new Date(a.createdAt) : new Date(),
        experience: a?.applicant?.yearsOfExperience ? `${a.applicant.yearsOfExperience} years` : '—',
        skills: Array.isArray(a?.applicant?.skills) ? a.applicant.skills : [],
        resume: a?.resume || '',
        coverLetter: a?.coverLetter || '',
        expectedSalary: a?.applicant?.expectedSalary ? `$${a.applicant.expectedSalary}` : '',
        location: formatLocation(a?.job?.location),
      }));

      setApplications(mapped);
      setTotalCount(pagination?.total ?? mapped.length);

      // Build filter options from results
      const jobs = [];
      const employers = [];
      mapped.forEach((m) => {
        if (m.jobId && !jobs.find((j) => j.id === m.jobId)) jobs.push({ id: m.jobId, title: m.jobTitle || '—' });
        if (m.employerId && !employers.find((e) => e.id === m.employerId)) employers.push({ id: m.employerId, name: m.employerName || '—' });
      });
      setJobsOptions(jobs);
      setEmployersOptions(employers);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setSnackbar({ open: true, message: 'Failed to load applications', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, jobFilter, employerFilter]);

  // Load data when filters/pagination change
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Subscribe to real-time application events on mount
  useEffect(() => {
    const unsubscribeJobApplication = socketService.subscribe('job_application', (data) => {
      setApplications(prev => {
        const newApplication = {
          id: data.applicationId,
          applicantName: data.applicantName,
          applicantEmail: data.applicantEmail,
          applicantPhone: data.applicantPhone || '',
          jobTitle: data.jobTitle,
          jobId: data.jobId,
          employerName: data.employerName,
          employerId: data.employerId,
          status: 'pending',
          appliedDate: new Date(),
          experience: data.experience || '',
          skills: data.skills || [],
          resume: data.resumeUrl || '',
          coverLetter: data.coverLetter || '',
          expectedSalary: data.expectedSalary || '',
          location: formatLocation(data.location)
        };
        return [newApplication, ...prev];
      });

      setSnackbar({
        open: true,
        message: `New application received from ${data.applicantName} for ${data.jobTitle}`,
        severity: 'info'
      });
    });

    return () => {
      unsubscribeJobApplication();
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'shortlisted': return 'warning';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <ApprovedIcon />;
      case 'rejected': return <RejectedIcon />;
      case 'shortlisted': return <ShortlistedIcon />;
      case 'pending': return <PendingIcon />;
      default: return <PendingIcon />;
    }
  };

  const statusLabels = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    shortlisted: 'Shortlisted',
    interview_scheduled: 'Interview Scheduled',
    interviewed: 'Interviewed',
    offer_extended: 'Offer Extended',
    accepted: 'Accepted',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn'
  };

  const handleUpdateStatus = async () => {
    if (!selectedApplication || !statusUpdate) return;
    try {
      const resp = await ApiService.updateApplicationStatus(selectedApplication.id, statusUpdate);
      const updated = resp.application || resp?.data?.application;

      setApplications(prev => prev.map(a => (
        a.id === selectedApplication.id ? { ...a, status: updated?.status || statusUpdate } : a
      )));

      setSelectedApplication(prev => prev ? { ...prev, status: updated?.status || statusUpdate } : prev);

      setSnackbar({
        open: true,
        message: `Status updated to ${statusLabels[updated?.status || statusUpdate]}`,
        severity: 'success'
      });
      setStatusUpdate('');
    } catch (error) {
      console.error('Admin failed to update application status:', error);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.applicantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.applicantEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.employerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesJob = jobFilter === 'all' || app.jobId === jobFilter;
    const matchesEmployer = employerFilter === 'all' || app.employerId === employerFilter;
    
    return matchesSearch && matchesStatus && matchesJob && matchesEmployer;
  });

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setDialogOpen(true);
  };

  // Status updates are handled via admin actions on detail dialogs or server-side

  const handleExportData = () => {
    // Implement export functionality
    setSnackbar({
      open: true,
      message: 'Export functionality will be implemented',
      severity: 'info'
    });
  };

  const stats = {
    total: totalCount || applications.length,
    pending: applications.filter(app => app.status === 'pending').length,
    shortlisted: applications.filter(app => app.status === 'shortlisted').length,
    approved: applications.filter(app => app.status === 'approved').length,
    rejected: applications.filter(app => app.status === 'rejected').length
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AssignmentIcon />
        All Job Applications
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        System-wide job application management and monitoring
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">Total Applications</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="grey.600">{stats.pending}</Typography>
              <Typography variant="body2" color="text.secondary">Pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">{stats.shortlisted}</Typography>
              <Typography variant="body2" color="text.secondary">Shortlisted</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">{stats.approved}</Typography>
              <Typography variant="body2" color="text.secondary">Approved</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">{stats.rejected}</Typography>
              <Typography variant="body2" color="text.secondary">Rejected</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="shortlisted">Shortlisted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Job</InputLabel>
              <Select
                value={jobFilter}
                label="Job"
                onChange={(e) => setJobFilter(e.target.value)}
              >
                <MenuItem value="all">All Jobs</MenuItem>
                {jobsOptions.map(job => (
                  <MenuItem key={job.id} value={job.id}>{job.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Employer</InputLabel>
              <Select
                value={employerFilter}
                label="Employer"
                onChange={(e) => setEmployerFilter(e.target.value)}
              >
                <MenuItem value="all">All Employers</MenuItem>
                {employersOptions.map(employer => (
                  <MenuItem key={employer.id} value={employer.id}>{employer.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportData}
              >
                Export
              </Button>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setJobFilter('all');
                  setEmployerFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Applications Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Applicant</TableCell>
                <TableCell>Job Position</TableCell>
                <TableCell>Employer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Applied Date</TableCell>
                <TableCell>Experience</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : filteredApplications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">No applications found</TableCell>
                </TableRow>
              ) : (
                filteredApplications
                  .map((application) => (
                    <TableRow key={application.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar>
                            {application.applicantName.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">{application.applicantName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {application.applicantEmail}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{application.jobTitle}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ID: {application.jobId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{application.employerName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(application.status)}
                          label={application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                          color={getStatusColor(application.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {application.appliedDate.toLocaleDateString()}
                      </TableCell>
                      <TableCell>{application.experience}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewApplication(application)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Contact Applicant">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`mailto:${application.applicantEmail}`)}
                            >
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount || filteredApplications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Application Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon />
            Application Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon />
                      Applicant Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography><strong>Name:</strong> {selectedApplication.applicantName}</Typography>
                    <Typography><strong>Email:</strong> {selectedApplication.applicantEmail}</Typography>
                    <Typography><strong>Phone:</strong> {selectedApplication.applicantPhone}</Typography>
                    <Typography><strong>Experience:</strong> {selectedApplication.experience}</Typography>
                    <Typography><strong>Expected Salary:</strong> {selectedApplication.expectedSalary}</Typography>
                    <Typography><strong>Location:</strong> {formatLocation(selectedApplication.location)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkIcon />
                      Job Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography><strong>Position:</strong> {selectedApplication.jobTitle}</Typography>
                    <Typography><strong>Job ID:</strong> {selectedApplication.jobId}</Typography>
                    <Typography><strong>Employer:</strong> {selectedApplication.employerName}</Typography>
                    <Typography><strong>Applied Date:</strong> {selectedApplication.appliedDate.toLocaleDateString()}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography><strong>Status:</strong></Typography>
                      <Chip
                        icon={getStatusIcon(selectedApplication.status)}
                        label={selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                        color={getStatusColor(selectedApplication.status)}
                        size="small"
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Update Status</InputLabel>
                        <Select
                          value={statusUpdate}
                          label="Update Status"
                          onChange={(e) => setStatusUpdate(e.target.value)}
                        >
                          {Object.entries(statusLabels).map(([key, label]) => (
                            <MenuItem key={key} value={key}>{label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button variant="contained" size="small" disabled={!statusUpdate} onClick={handleUpdateStatus}>Save</Button>
                        <Button variant="text" size="small" onClick={() => setStatusUpdate('')}>Reset</Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Skills</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedApplication.skills.map((skill, index) => (
                        <Chip key={index} label={skill} variant="outlined" />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Cover Letter</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography>{selectedApplication.coverLetter}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {selectedApplication && (
            <>
              <Button
                variant="outlined"
                onClick={() => window.open(`mailto:${selectedApplication.applicantEmail}`)}
                startIcon={<EmailIcon />}
              >
                Contact
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  // Implement resume download
                  setSnackbar({
                    open: true,
                    message: 'Resume download functionality will be implemented',
                    severity: 'info'
                  });
                }}
                startIcon={<DownloadIcon />}
              >
                Download Resume
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllJobApplications;